import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Play, Pause, Volume2, Download } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth handled via Bearer token in axios interceptor (App.js)

function AudioPlayer({ entryId }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showFullPlayer, setShowFullPlayer] = useState(false);
  const [error, setError] = useState(null);
  const audioElementRef = useRef(null);
  const progressRef = useRef(null);
  const audioUrlRef = useRef(null);

  // Keep audioUrlRef in sync with audioUrl state
  useEffect(() => {
    audioUrlRef.current = audioUrl;
  }, [audioUrl]);

  // Cleanup audio when component unmounts (navigating away)
  useEffect(() => {
    return () => {
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.src = '';
        audioElementRef.current = null;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
    };
  }, []); // Only run cleanup on unmount

  const loadAudio = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/journal/${entryId}/audio`, {
        responseType: 'blob',
        validateStatus: (status) => status < 500
      });
      
      if (response.status === 404 || response.status >= 400) {
        setError('Audio file not available');
        setIsLoading(false);
        return;
      }
      
      const url = URL.createObjectURL(response.data);
      setAudioUrl(url);
      
      const audio = new Audio(url);
      audioElementRef.current = audio;
      
      // Set up event handlers
      audio.onloadedmetadata = () => {
        setDuration(audio.duration);
      };
      
      audio.ontimeupdate = () => {
        setCurrentTime(audio.currentTime);
      };
      
      audio.onended = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };
      
      audio.onerror = () => {
        setError('Failed to load audio');
        setIsLoading(false);
      };

      // Wait for audio to be ready
      audio.oncanplaythrough = async () => {
        setShowFullPlayer(true);
        setIsLoading(false);
        try {
          await audio.play();
          setIsPlaying(true);
        } catch (playError) {
          console.error('Error playing audio:', playError);
        }
      };
      
      // Trigger load
      audio.load();
      
    } catch (err) {
      console.error('Error loading audio:', err);
      setError('Failed to load audio');
      setIsLoading(false);
    }
  };

  const togglePlayPause = async () => {
    if (!audioElementRef.current) {
      await loadAudio();
      return;
    }
    
    try {
      if (isPlaying) {
        audioElementRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioElementRef.current.play();
        setIsPlaying(true);
      }
    } catch (err) {
      console.error('Error toggling play/pause:', err);
    }
  };

  const handleProgressClick = (e) => {
    if (!audioElementRef.current || !progressRef.current || duration === 0) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, clickX / width));
    const newTime = percentage * duration;
    
    audioElementRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time) => {
    if (isNaN(time) || time === 0) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Get auth token from localStorage (stored as 'noyo_token')
      const token = localStorage.getItem('noyo_token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API}/journal/${entryId}/audio/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download response error:', response.status, errorText);
        throw new Error(`Download failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('Empty file received');
      }
      
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `noyo_recording_${entryId}.mp3`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('Error downloading audio:', err);
      alert(`Failed to download audio: ${err.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Error state - graceful fallback with download option
  if (error) {
    return (
      <div className="flex items-center justify-between" data-testid={`audio-error-${entryId}`}>
        <span className="text-xs text-muted-foreground">Audio unavailable</span>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => { setError(null); loadAudio(); }}
            className="text-xs text-primary hover:text-[#7A9186] transition-colors"
          >
            Retry
          </button>
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
            data-testid={`download-audio-${entryId}`}
          >
            {isDownloading ? (
              <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <Download className="w-3 h-3" />
            )}
            <span>Download</span>
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 text-primary">
        <Sparkles className="w-4 h-4 animate-pulse" />
        <span className="text-sm">Loading audio...</span>
      </div>
    );
  }

  // Simple button before audio is loaded
  if (!showFullPlayer) {
    return (
      <div className="flex items-center justify-between">
        <button
          onClick={loadAudio}
          className="flex items-center space-x-2 text-primary hover:text-[#7A9186] transition-colors"
          data-testid={`play-audio-${entryId}`}
        >
          <Play className="w-4 h-4" />
          <span className="text-sm">Play Audio</span>
        </button>
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="p-2 text-muted-foreground hover:text-primary transition-colors disabled:opacity-50 rounded-full hover:bg-secondary/50"
          data-testid={`download-audio-${entryId}`}
          title="Download as MP3"
        >
          {isDownloading ? (
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </button>
      </div>
    );
  }

  // Full player with scrub bar
  return (
    <div className="w-full" data-testid={`audio-player-${entryId}`}>
      <div className="flex items-center space-x-3">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlayPause}
          className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white hover:bg-primary/80 transition-colors flex items-center justify-center shadow-sm"
          data-testid={`toggle-audio-${entryId}`}
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </button>

        {/* Progress Bar */}
        <div className="flex-1 flex items-center space-x-3">
          <span className="text-xs text-muted-foreground w-10 text-right font-mono">
            {formatTime(currentTime)}
          </span>
          
          <div
            ref={progressRef}
            onClick={handleProgressClick}
            className="flex-1 h-2 bg-secondary rounded-full cursor-pointer relative overflow-hidden"
            data-testid={`audio-progress-${entryId}`}
          >
            <div
              className="absolute top-0 left-0 h-full bg-primary rounded-full transition-all duration-100"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          
          <span className="text-xs text-muted-foreground w-10 font-mono">
            {formatTime(duration)}
          </span>
        </div>

        {/* Volume Icon */}
        <Volume2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        
        {/* Download Button */}
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex-shrink-0 p-2 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
          data-testid={`download-audio-${entryId}`}
          title="Download audio"
        >
          {isDownloading ? (
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
}

export default AudioPlayer;
